import { useMemo, useState } from 'react'
import type { ChartPayload, Series, Unit } from '@pharmacia/core'
import { exact } from './format.ts'
import { Legend } from './plot-core.tsx'
import { colorFor, SOLO } from './seriesColor.ts'
import { useSize } from './useSize.ts'
import { LinePlot } from './LinePlot.tsx'
import { BarPlot } from './BarPlot.tsx'
import { HBarPlot } from './HBarPlot.tsx'
import { Multiples } from './Multiples.tsx'

type Def = { title: string; question: string; span?: 1 | 2 }

/**
 * One chart, framed: the question it answers, the plot, a legend, the note
 * that qualifies it, and the table underneath it.
 *
 * The table is not a nicety. Three of the light-mode series steps sit
 * below 3:1 against the card, which the palette permits only where colour
 * is never the sole carrier of meaning -- so every chart on this screen
 * offers its own numbers, and anyone who cannot separate two hues can
 * read them instead.
 */
export function ChartBlock({ def, payload, loading, error, onRetry }: {
  def: Def
  payload?: ChartPayload
  loading?: boolean
  error?: string
  onRetry?: () => void
}) {
  const [asTable, setAsTable] = useState(false)
  const { ref, width } = useSize<HTMLDivElement>()

  const table = useMemo(() => (payload ? derivedTable(payload) : null), [payload])
  const hasPlot = payload ? payload.form !== 'tiles' && payload.form !== 'table' : false

  return (
    <section className={`viz-card ${def.span === 2 ? 'span-2' : ''}`}>
      <header>
        <div className="grow" style={{ minWidth: 0 }}>
          <h3>{def.title}</h3>
          <p className="q">{def.question}</p>
        </div>
        {table && hasPlot && (
          <button
            type="button" className="viz-toggle" aria-pressed={asTable}
            onClick={() => setAsTable(v => !v)}
          >
            {asTable ? 'Chart' : 'Table'}
          </button>
        )}
      </header>

      {payload?.tiles?.length ? (
        <div className="viz-tiles">
          {payload.tiles.map(t => (
            <div className="viz-tile" key={t.label}>
              <div className="k">{t.label}</div>
              <div className="v">{t.value}</div>
              {t.sub && <div className="s">{t.sub}</div>}
            </div>
          ))}
        </div>
      ) : null}

      <div className="plot" ref={ref}>
        {loading && <div className="viz-skel" />}
        {!loading && error && (
          <div className="viz-empty">
            <div>
              <div>{error}</div>
              {onRetry && (
                <button type="button" className="viz-toggle" style={{ marginTop: 10 }} onClick={onRetry}>
                  Try again
                </button>
              )}
            </div>
          </div>
        )}
        {!loading && !error && payload && (
          asTable || !hasPlot
            ? (table
              ? <DataTable columns={table.columns} rows={table.rows} />
              : <div className="viz-empty">Nothing to show for these filters.</div>)
            : width > 0 && <Plot payload={payload} width={width} />
        )}
      </div>

      {!loading && !error && payload && !asTable && hasPlot && payload.form !== 'multiples' && (
        <Legend items={legendItems(payload)} />
      )}

      {payload?.note && !loading && !error && <p className="note">{payload.note}</p>}
    </section>
  )
}

function legendItems(p: ChartPayload): { color: string; label: string }[] {
  if (p.series.length < 2) return []
  return p.series.map(s => ({ color: colorFor(p.id, s.key), label: s.label }))
}

/** Exported so the render check can put every real payload through it. */
export function Plot({ payload, width }: { payload: ChartPayload; width: number }) {
  const { id, form, categories, series, unit } = payload
  const empty = series.length === 0
    || series.every(s => s.data.every(v => v === null || v === 0))

  if (empty) {
    return <div className="viz-empty">Nothing recorded for these filters.</div>
  }

  switch (form) {
    case 'line':
      return <LinePlot id={id} categories={categories} series={series} unit={unit} width={width} />
    case 'bar':
    case 'histogram':
      return <BarPlot id={id} categories={categories} series={series} unit={unit} width={width}
                      height={form === 'histogram' ? 200 : 220} />
    case 'stacked':
      return <BarPlot id={id} categories={categories} series={series} unit={unit} width={width} stacked />
    case 'hbar':
      return <HBarPlot categories={categories} series={series} unit={unit} width={width} />
    case 'funnel':
      // A funnel is an ordered hbar whose stages are subsets of the first,
      // so each bar also says what share of the top it kept.
      return <HBarPlot categories={categories} series={series} unit={unit} width={width} share />
    case 'multiples':
      return <Multiples id={id} categories={categories} series={series} unit={unit} />
    default:
      return null
  }
}

export function DataTable({ columns, rows }: {
  columns: string[]
  rows: (string | number | null)[][]
}) {
  if (!rows.length) return <div className="viz-empty">Nothing to show for these filters.</div>
  return (
    <div className="viz-table-wrap">
      <table className="viz-table">
        <thead><tr>{columns.map(c => <th key={c} scope="col">{c}</th>)}</tr></thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={i}>{r.map((cell, j) => <td key={j}>{cell ?? '—'}</td>)}</tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

/**
 * The table behind the chart.
 *
 * A chart that ships its own table uses it; everything else gets one built
 * from the series, so the toggle is never missing where the colour caveat
 * says it must exist.
 */
function derivedTable(p: ChartPayload): { columns: string[]; rows: (string | number | null)[][] } | null {
  if (p.table) return p.table
  if (!p.series.length || !p.categories.length) return null

  const unitOf = (s: Series): Unit => s.unit ?? p.unit
  return {
    columns: [timeAxisLabel(p), ...p.series.map(s => s.label)],
    rows: p.categories.map((c, i) => [c, ...p.series.map(s => exact(s.data[i] ?? null, unitOf(s)))]),
  }
}

const timeAxisLabel = (p: ChartPayload) =>
  p.form === 'line' || p.form === 'bar' || p.form === 'stacked' ? 'Period' : 'Category'

export { SOLO }
